import { isNonEmptyArray } from '../../Array';
import {
  AdvanceId,
  brandedId,
  DisbursementId,
  DrawId,
  EventId,
  FeeId,
  LedgerPostingId,
  LedgerTransactionId,
  PaymentDueId,
  PaymentId,
  PayoffId,
  PrincipalModificationId,
  WriteoffId,
} from '../../BrandedIds';
import type { PaymentDue } from '../../PaymentDue';
import { jsPlainDateToTemporalPlainDate, Temporal, toTemporalInstant } from '../../Temporal';
import { add, subtract } from '../../utils/math';
import { type Advance, asAdvanceId, type Fee } from '../Charges';
import type { Disbursement } from '../Disbursement';
import type { Draw } from '../Draw';
import type { InitialBalances } from '../InitialBalances';
import type { LoanConfiguration } from '../Loan';
import type { PrincipalModification } from '../Modification';
import { isReversalPayment, type Payment } from '../Payments';
import type { Payoff } from '../Payoffs';
import type { Writeoff } from '../Writeoff';
import { allocatePartialPayoffForLedger, allocatePaymentsForLedger, allocatePayoffForLedger } from './allocate';
import { LedgerIndex } from './LedgerIndex';
import { type Posting, REVERSAL_KIND_BY_ID_FIELD, type ReversalScopeIdField } from './Posting';
import type { Transaction } from './Transaction';

export type LedgerEventContext = {
  eventId: EventId;
  effectiveDate: Temporal.PlainDate;
  recordedAt: Temporal.Instant;
};

// Ambient controls live in `@willow/shared-node` (AsyncLocalStorage,
// Node-only); it injects its accessor at module load. The default
// here is "enabled" so tests / browser builds / migration scripts
// work without setup.
export type LedgerControls = {
  ledgerEnabled: boolean;
};

const ENABLED_CONTROLS: LedgerControls = { ledgerEnabled: true };

let controlsAccessor: () => LedgerControls = () => ENABLED_CONTROLS;

export const setLedgerControlsAccessor = (fn: () => LedgerControls): void => {
  controlsAccessor = fn;
};

export const getLedgerControls = (): LedgerControls => controlsAccessor();

export type LedgerErrorContext = {
  source: 'open';
};

export type LedgerErrorHandler = (err: unknown, ctx: LedgerErrorContext) => void;

const RETHROWING_HANDLER: LedgerErrorHandler = (err) => {
  throw err;
};

let errorHandler: LedgerErrorHandler = RETHROWING_HANDLER;

export const setLedgerErrorHandler = (fn: LedgerErrorHandler): void => {
  errorHandler = fn;
};

const handleLedgerError = (err: unknown, ctx: LedgerErrorContext): void => errorHandler(err, ctx);

export type StatementReceivableAmounts = {
  paymentDueId: PaymentDueId;
  principalReceivable?: number;
  interestReceivable?: number;
  escrowReceivable?: number;
  delinquentInterestReceivable?: number;
};

// Carries the *source* `paymentId` (the payment whose cash is parked
// in suspense), not the new payment triggering the rollout.
export type SuspenseRollout = StatementReceivableAmounts & {
  paymentId: PaymentId;
};

export type LedgerPaymentAllocation = {
  statements?: ReadonlyArray<StatementReceivableAmounts>;
  // DR suspense / CR statement receivable, one entry per
  // (source payment, target statement) pair.
  fromSuspense?: ReadonlyArray<SuspenseRollout>;
  // Reallocation from a specific balance account named by
  // `payment.debitSource`; capped at `debitSource.amount` and
  // attributed to the new paymentId (unlike `fromSuspense`).
  fromDebitSource?: {
    balance: 'unappliedSuspense' | 'futureStatementSuspense' | 'prePetitionUnappliedSuspense';
    statements: ReadonlyArray<StatementReceivableAmounts>;
  };
  // Partial-payment buffer (distinct from `futureStatementSuspense`,
  // the borrower's explicit "save for next statement" designation).
  suspense?: number;
  futureStatementSuspense?: number;
  unappliedSuspense?: number;
  additionalEscrow?: number;
  // Statements cleared with cash left over (+ any
  // `payment.additionalPrincipal`).
  curtailment?: number;
  // Recorder picks `feePaymentApplication` vs. the standalone variant
  // based on whether `paymentDueId` is set.
  fees?: ReadonlyArray<{ feeId: FeeId; paymentDueId?: PaymentDueId; amount: number }>;
  // Each entry emits `corporateAdvancePaymentApplication` (DR
  // `servicingClearing`) paired with `advanceReimbursement`. The
  // borrower's contribution lands in `servicingClearing`, not `cash`.
  advances?: ReadonlyArray<{ advanceId: AdvanceId; principalAmount: number; interestAmount: number }>;
};

export type LedgerPayoffAllocation = {
  statements: ReadonlyArray<StatementReceivableAmounts>;
  remainingPrincipal: number;
  // Assessed + applied atomically; the receivable doesn't outlive
  // the payoff event.
  payoffFee?: {
    feeId: FeeId;
    paymentDueId?: PaymentDueId;
    amount: number;
  };
  // Legacy `outstandingInterest` positive case — unbilled interest
  // through the payoff date.
  outstandingInterest?: number;
  // Legacy `outstandingInterest` negative case — already-paid interest
  // past the payoff date. Always positive when present (the sign
  // is encoded in the posting kind).
  outstandingInterestRebate?: number;
  // Per-bucket split — `Payoff.suspenseBalance` is a SUM and draining
  // the wrong bucket would drive it negative. Drain order:
  // `suspense` → `unappliedSuspense` → `futureStatementSuspense`.
  suspenseApplied?: {
    suspense?: number;
    unappliedSuspense?: number;
    futureStatementSuspense?: number;
  };
  // `shortageAction: 'waive-suspense'` parks the shortfall in
  // `payoffShortfall` pending writeoff via `payoffShortfallChargeoff`.
  payoffShortfall?: number;
};

// Partial payoffs don't clear receivables or carry outstandingInterest
// adjustments; borrower-designated additionals ride along on the
// same payment.
export type LedgerPartialPayoffAllocation = {
  // The negotiated partial-payoff amount (reuses
  // `payoffPrincipalApplication` so GL Codes sum partial + full).
  partialPrincipal: number;
  payoffFee?: {
    feeId: FeeId;
    paymentDueId?: PaymentDueId;
    amount: number;
  };
  // Borrower-designated extra curtailment (no payoffId attribution).
  additionalPrincipal?: number;
  additionalEscrow?: number;
  additionalSuspense?: number;
};

const newTransactionId = (): LedgerTransactionId => brandedId('LedgerTransactionId') as LedgerTransactionId;

const newPostingId = (): LedgerPostingId => brandedId('LedgerPostingId') as LedgerPostingId;

// Balance-source reallocations have `payment.total === 0` and carry
// the amount on `debitSource`.
const paymentAmount = (payment: Payment): number =>
  payment.debitSource?.type === 'balance' ? payment.debitSource.amount : payment.total;

const StatementReceivableKeys = [
  'principalReceivable',
  'interestReceivable',
  'escrowReceivable',
  'delinquentInterestReceivable',
] as const;

const PAYMENT_REVERSAL_KIND_MAP: Readonly<Record<string, string>> = REVERSAL_KIND_BY_ID_FIELD.paymentId ?? {};
const REVERSIBLE_PAYMENT_KIND_SET: ReadonlySet<string> = new Set(Object.keys(PAYMENT_REVERSAL_KIND_MAP));
type ReversiblePaymentKind = string;

export class LoanLedger {
  /**
   * Production entry point for handler-driven ledger emission. Honors
   * the ambient {@link LedgerControls} kill switch; errors thrown by
   * the callback are funneled to {@link setLedgerErrorHandler}.
   *
   * The factory-in-callback shape (`at(ctx)` instead of `ctx` as a
   * direct arg) is deliberate: ctx-builder expressions evaluate inside
   * the callback, so a throw there is caught. With ctx as a direct
   * arg, it would evaluate at the call site outside the safety net.
   *
   * `deriveLedgerFromSnapshot` and `derivePayment` use
   * {@link LoanLedger.forDerive} instead — derive paths want errors to
   * propagate so parity tests catch bugs.
   */
  static open(transactions: Transaction[], fn: (at: (ctx: LedgerEventContext) => LoanLedger) => void): void {
    if (!getLedgerControls().ledgerEnabled) return;
    let instance: LoanLedger | undefined;
    const at = (ctx: LedgerEventContext): LoanLedger => {
      if (!instance) instance = new LoanLedger(transactions, ctx);
      else instance.withContext(ctx);
      return instance;
    };
    try {
      fn(at);
    } catch (err) {
      handleLedgerError(err, { source: 'open' });
    }
  }

  /**
   * Direct-instance entry used by derive paths: bypasses the
   * {@link LedgerControls} kill switch and the {@link open} error
   * trap so callers see failures immediately. The constructor is
   * protected; production code routes through this factory or
   * {@link open}. Tests use {@link TestLoanLedger}.
   */
  static forDerive(transactions: Transaction[], ctx: LedgerEventContext): LoanLedger {
    return new LoanLedger(transactions, ctx);
  }

  public readonly index: LedgerIndex;

  protected constructor(
    private readonly ledger: Transaction[],
    private ctx: LedgerEventContext,
  ) {
    this.index = new LedgerIndex(ledger);
  }

  /**
   * Swap the per-transaction context. Used by derivation to replay many
   * events through one long-lived instance so the cached index survives
   * the whole replay.
   */
  withContext(ctx: Partial<LedgerEventContext>): this {
    this.ctx = { ...this.ctx, ...ctx };
    return this;
  }

  get transactions(): ReadonlyArray<Transaction> {
    return this.ledger;
  }

  private isVoided(postingId: LedgerPostingId): boolean {
    return this.index.isVoided(postingId);
  }

  // `interest`, `principalPrepayment`, `mortgageInsurance`, `fee` on
  // `InitialBalances` are intentionally NOT boarded — no dedicated
  // posting kind. See `Posting.ts` boarding comments.
  recordBoardingInitialBalances(balances: InitialBalances, originalPrincipal: number): Transaction | undefined {
    const postings: Posting[] = [];

    const outstandingPrincipal = Math.max(subtract(originalPrincipal, balances.principal), 0);
    if (outstandingPrincipal > 0) {
      postings.push({
        kind: 'boardingPrincipal',
        id: newPostingId(),
        amount: outstandingPrincipal,
        debitAccount: 'principal',
        creditAccount: 'noteHolder',
      });
    }
    if (balances.nonInterestBearingUnpaidPrincipal && balances.nonInterestBearingUnpaidPrincipal > 0) {
      postings.push({
        kind: 'boardingDeferredPrincipal',
        id: newPostingId(),
        amount: balances.nonInterestBearingUnpaidPrincipal,
        debitAccount: 'deferredPrincipal',
        creditAccount: 'noteHolder',
      });
    }
    if (balances.escrow > 0) {
      postings.push({
        kind: 'boardingEscrow',
        id: newPostingId(),
        amount: balances.escrow,
        debitAccount: 'cash',
        creditAccount: 'escrow',
      });
    }
    if (balances.futureStatementSuspense > 0) {
      postings.push({
        kind: 'boardingSuspense',
        id: newPostingId(),
        amount: balances.futureStatementSuspense,
        debitAccount: 'cash',
        creditAccount: 'futureStatementSuspense',
      });
    }
    if (balances.unappliedSuspense > 0) {
      postings.push({
        kind: 'boardingSuspense',
        id: newPostingId(),
        amount: balances.unappliedSuspense,
        debitAccount: 'cash',
        creditAccount: 'unappliedSuspense',
      });
    }
    if (balances.holdback && balances.holdback > 0) {
      postings.push({
        kind: 'boardingHoldback',
        id: newPostingId(),
        amount: balances.holdback,
        debitAccount: 'cash',
        creditAccount: 'holdback',
      });
    }
    if (balances.reserve && balances.reserve > 0) {
      postings.push({
        kind: 'boardingReserve',
        id: newPostingId(),
        amount: balances.reserve,
        debitAccount: 'cash',
        creditAccount: 'reserve',
      });
    }

    return this.appendTransaction(postings);
  }

  // Post-boarding `initialBalances` mutations. `handleUpdateLoan`
  // can edit any of these fields after the loan has boarded; the
  // ledger needs a posting per moved field so the GL balances track.
  // Sign of the delta picks DR/CR — positive delta mirrors the
  // boarding direction; negative swaps. Returns undefined for a
  // no-op (no field moved).
  //
  // Fields without boarding postings (`interest`, `mortgageInsurance`,
  // `principalPrepayment`, `fee`) are not corrected here — same
  // fidelity gap that already exists at boarding.
  recordBalanceCorrection(oldBalances: InitialBalances, newBalances: InitialBalances): Transaction | undefined {
    const postings: Posting[] = [];

    const principalDelta = subtract(newBalances.principal, oldBalances.principal);
    if (principalDelta !== 0) {
      // Boarding direction (positive delta): DR principal / CR noteHolder
      // (mirrors `boardingPrincipal`). Negative delta swaps.
      const positive = principalDelta > 0;
      postings.push({
        kind: 'balanceCorrectionPrincipal',
        id: newPostingId(),
        amount: Math.abs(principalDelta),
        debitAccount: positive ? 'principal' : 'noteHolder',
        creditAccount: positive ? 'noteHolder' : 'principal',
      });
    }

    const escrowDelta = subtract(newBalances.escrow, oldBalances.escrow);
    if (escrowDelta !== 0) {
      // Boarding direction: DR cash / CR escrow (mirrors `boardingEscrow`).
      const positive = escrowDelta > 0;
      postings.push({
        kind: 'balanceCorrectionEscrow',
        id: newPostingId(),
        amount: Math.abs(escrowDelta),
        debitAccount: positive ? 'cash' : 'escrow',
        creditAccount: positive ? 'escrow' : 'cash',
      });
    }

    const futureSuspenseDelta = subtract(newBalances.futureStatementSuspense, oldBalances.futureStatementSuspense);
    if (futureSuspenseDelta !== 0) {
      // Boarding direction: DR cash / CR futureStatementSuspense (mirrors `boardingSuspense`).
      const positive = futureSuspenseDelta > 0;
      postings.push({
        kind: 'balanceCorrectionSuspense',
        id: newPostingId(),
        amount: Math.abs(futureSuspenseDelta),
        debitAccount: positive ? 'cash' : 'futureStatementSuspense',
        creditAccount: positive ? 'futureStatementSuspense' : 'cash',
      });
    }

    const oldReserve = oldBalances.reserve ?? 0;
    const newReserve = newBalances.reserve ?? 0;
    const reserveDelta = subtract(newReserve, oldReserve);
    if (reserveDelta !== 0) {
      // Boarding direction: DR cash / CR reserve (mirrors `boardingReserve`).
      const positive = reserveDelta > 0;
      postings.push({
        kind: 'balanceCorrectionReserve',
        id: newPostingId(),
        amount: Math.abs(reserveDelta),
        debitAccount: positive ? 'cash' : 'reserve',
        creditAccount: positive ? 'reserve' : 'cash',
      });
    }

    const oldDeferred = oldBalances.nonInterestBearingUnpaidPrincipal ?? 0;
    const newDeferred = newBalances.nonInterestBearingUnpaidPrincipal ?? 0;
    const deferredDelta = subtract(newDeferred, oldDeferred);
    if (deferredDelta !== 0) {
      // Boarding direction: DR deferredPrincipal / CR noteHolder
      // (mirrors `boardingDeferredPrincipal`).
      const positive = deferredDelta > 0;
      postings.push({
        kind: 'balanceCorrectionDeferredPrincipal',
        id: newPostingId(),
        amount: Math.abs(deferredDelta),
        debitAccount: positive ? 'deferredPrincipal' : 'noteHolder',
        creditAccount: positive ? 'noteHolder' : 'deferredPrincipal',
      });
    }

    if (postings.length === 0) return undefined;
    return this.appendTransaction(postings);
  }

  recordStatementAssessment(paymentDue: PaymentDue): Transaction | undefined {
    return this.appendTransaction(this.buildStatementAssessmentPostings(paymentDue));
  }

  // Emits an atomic cancel-then-re-assess transaction. Does NOT touch
  // any `statementPaymentApplication` postings that may have settled
  // the original assessment from cash — if a fulfilled payment's
  // allocation needs reshuffling, the caller must invoke
  // `recordPaymentReversal` separately.
  recordStatementRegeneration(newPaymentDue: PaymentDue): Transaction | undefined {
    const paymentDueId = newPaymentDue.id;

    const postings: Posting[] = [];
    for (const txn of this.ledger) {
      for (const p of txn.postings) {
        if (p.kind !== 'statementAssessment') continue;
        if (!('paymentDueId' in p) || p.paymentDueId !== paymentDueId) continue;
        if (this.isVoided(p.id)) continue;
        postings.push({
          ...(p as object),
          kind: 'statementCancellation',
          id: newPostingId(),
          debitAccount: p.creditAccount,
          creditAccount: p.debitAccount,
          voidsPostingId: p.id,
        } as unknown as Posting);
      }
    }

    postings.push(...this.buildStatementAssessmentPostings(newPaymentDue));
    return this.appendTransaction(postings);
  }

  private buildStatementAssessmentPostings(paymentDue: PaymentDue): Posting[] {
    const postings: Posting[] = [];
    const paymentDueId = paymentDue.id;

    if (paymentDue.principal > 0) {
      postings.push({
        kind: 'statementAssessment',
        id: newPostingId(),
        amount: paymentDue.principal,
        debitAccount: 'principalReceivable',
        creditAccount: 'principal',
        paymentDueId,
      });
    }
    // `paymentDue.interest` is the combined interest at regen time;
    // split into standard and DI receivable legs so the DI portion
    // can be waived / deferred / charged-off on its own.
    const diAmount = (paymentDue.attribution ?? [])
      .filter((a) => a.type === 'delinquentInterest' && !a.isWaived && !a.isDeferred)
      .reduce((sum, a) => sum + a.interest, 0);
    const standardInterest = paymentDue.interest - diAmount;
    if (standardInterest > 0) {
      postings.push({
        kind: 'statementAssessment',
        id: newPostingId(),
        amount: standardInterest,
        debitAccount: 'interestReceivable',
        creditAccount: 'interest',
        paymentDueId,
      });
    }
    if (paymentDue.escrow > 0) {
      postings.push({
        kind: 'statementAssessment',
        id: newPostingId(),
        amount: paymentDue.escrow,
        debitAccount: 'escrowReceivable',
        // Placeholder liability; converts to `escrow` on payment via
        // `escrowObligationSettlement`. See GLAccount.ts.
        creditAccount: 'escrowObligation',
        paymentDueId,
      });
    }
    if (diAmount > 0) {
      postings.push({
        kind: 'statementAssessment',
        id: newPostingId(),
        amount: diAmount,
        debitAccount: 'delinquentInterestReceivable',
        creditAccount: 'delinquentInterest',
        paymentDueId,
      });
    }
    return postings;
  }

  // `holdback` is not yet ledgered — no payment-time posting kind
  // exists for it (only the `boardingHoldback` kind).
  recordDepositLineItems(
    paymentId: PaymentId,
    lineItems: {
      additionalEscrow?: number;
      additionalPrincipal?: number;
      additionalSuspense?: number;
    },
  ): Transaction | undefined {
    const postings: Posting[] = [];

    if (lineItems.additionalEscrow && lineItems.additionalEscrow > 0) {
      postings.push({
        kind: 'paymentEscrowContribution',
        id: newPostingId(),
        amount: lineItems.additionalEscrow,
        debitAccount: 'cash',
        creditAccount: 'escrow',
        paymentId,
      });
    }
    if (lineItems.additionalPrincipal && lineItems.additionalPrincipal > 0) {
      postings.push({
        kind: 'curtailment',
        id: newPostingId(),
        amount: lineItems.additionalPrincipal,
        debitAccount: 'cash',
        creditAccount: 'principal',
        paymentId,
      });
    }
    if (lineItems.additionalSuspense && lineItems.additionalSuspense > 0) {
      postings.push({
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount: lineItems.additionalSuspense,
        debitAccount: 'cash',
        creditAccount: 'unappliedSuspense',
        paymentId,
      });
    }

    return this.appendTransaction(postings);
  }

  recordPrincipalPrepayment(payment: Payment): Transaction | undefined {
    if (payment.total <= 0) return undefined;
    return this.appendTransaction([
      {
        kind: 'curtailment',
        id: newPostingId(),
        amount: payment.total,
        debitAccount: 'cash',
        creditAccount: 'principal',
        paymentId: payment.id,
      },
    ]);
  }

  // `modification.amount` is the delta to principal — negative is a
  // writedown (offset by collectionType), positive is a writeup
  // re-recognising prior chargeoff.
  recordPrincipalModification(modification: PrincipalModification): Transaction | undefined {
    if (modification.amount === 0) return undefined;
    const amount = Math.abs(modification.amount);
    const isWriteup = modification.amount > 0;
    const debitAccount = isWriteup
      ? 'principal'
      : modification.collectionType === 'deferred'
        ? 'deferredPrincipal'
        : 'modification';
    const creditAccount = isWriteup ? 'chargeoff' : 'principal';
    return this.appendTransaction([
      {
        kind: 'principalModification',
        id: newPostingId(),
        amount,
        debitAccount,
        creditAccount,
        modificationId: modification.id,
      },
    ]);
  }

  recordPrincipalModificationReversal(reversedModificationId: PrincipalModificationId): Transaction | undefined {
    return this.recordReversalsFor('modificationId', reversedModificationId);
  }

  // Returns transactions in input order: primary payment leads,
  // subsequent entries are siblings (e.g., auto-reserve top-ups).
  recordPaymentApplications(
    payments: ReadonlyArray<Payment>,
    outstanding: ReadonlyArray<PaymentDue>,
    outstandingFees: ReadonlyArray<Fee> = [],
    outstandingAdvances: ReadonlyArray<Advance> = [],
    configuration?: LoanConfiguration,
  ): ReadonlyArray<Transaction> {
    const suspenseSources = this.index.suspenseSources();
    const allocations = allocatePaymentsForLedger(
      payments,
      outstanding,
      suspenseSources,
      outstandingFees,
      outstandingAdvances,
      this.index,
      configuration,
    );
    const txns: Transaction[] = [];
    for (const { payment, allocation } of allocations) {
      const txn = this.recordPaymentApplication(payment, allocation);
      if (txn) txns.push(txn);
    }
    return txns;
  }

  // Callers that don't already have a `LedgerPaymentAllocation`
  // should prefer `recordPaymentApplications` (plural).
  recordPaymentApplication(payment: Payment, allocation: LedgerPaymentAllocation): Transaction | undefined {
    const postings: Posting[] = [];
    const paymentId = payment.id;

    // Reserve-method payments draw from the loan's `reserve` balance
    // rather than new cash inflow; only the statement + curtailment
    // legs apply.
    if (payment.method === 'reserve') {
      if (allocation.statements) {
        for (const stmt of allocation.statements) {
          for (const key of StatementReceivableKeys) {
            const amt = stmt[key];
            if (!amt || amt <= 0) continue;
            postings.push({
              kind: 'reserveStatementApplication',
              id: newPostingId(),
              amount: amt,
              debitAccount: 'reserve',
              creditAccount: key,
              paymentDueId: stmt.paymentDueId,
            });
          }
        }
      }
      if (allocation.curtailment && allocation.curtailment > 0) {
        postings.push({
          kind: 'reservePrincipalApplication',
          id: newPostingId(),
          amount: allocation.curtailment,
          debitAccount: 'reserve',
          creditAccount: 'principal',
        });
      }
      return this.appendTransaction(postings);
    }

    if (allocation.statements) {
      for (const stmt of allocation.statements) {
        for (const key of StatementReceivableKeys) {
          const amt = stmt[key];
          if (amt && amt > 0) {
            postings.push({
              kind: 'statementPaymentApplication',
              id: newPostingId(),
              amount: amt,
              debitAccount: 'cash',
              creditAccount: key,
              paymentId,
              paymentDueId: stmt.paymentDueId,
            });
            // Convert placeholder `escrowObligation` (from the
            // assessment) into real trust-held `escrow` now that the
            // borrower has paid the escrow portion.
            if (key === 'escrowReceivable') {
              postings.push({
                kind: 'escrowObligationSettlement',
                id: newPostingId(),
                amount: amt,
                debitAccount: 'escrowObligation',
                creditAccount: 'escrow',
                paymentId,
                paymentDueId: stmt.paymentDueId,
              });
            }
          }
        }
      }
    }

    if (allocation.fromSuspense) {
      for (const rollout of allocation.fromSuspense) {
        for (const key of StatementReceivableKeys) {
          const amt = rollout[key];
          if (amt && amt > 0) {
            postings.push({
              kind: 'suspenseStatementApplication',
              id: newPostingId(),
              amount: amt,
              debitAccount: 'suspense',
              creditAccount: key,
              // Attribute to the *source* payment whose suspense is
              // being drained, not the new payment triggering the
              // rollout.
              paymentId: rollout.paymentId,
              paymentDueId: rollout.paymentDueId,
            });
            if (key === 'escrowReceivable') {
              postings.push({
                kind: 'escrowObligationSettlement',
                id: newPostingId(),
                amount: amt,
                debitAccount: 'escrowObligation',
                creditAccount: 'escrow',
                paymentId: rollout.paymentId,
                paymentDueId: rollout.paymentDueId,
              });
            }
          }
        }
      }
    }

    if (allocation.fromDebitSource) {
      const { balance, statements } = allocation.fromDebitSource;
      for (const stmt of statements) {
        for (const key of StatementReceivableKeys) {
          const amt = stmt[key];
          if (amt && amt > 0) {
            postings.push({
              kind: 'suspenseStatementApplication',
              id: newPostingId(),
              amount: amt,
              debitAccount: balance,
              creditAccount: key,
              paymentId,
              paymentDueId: stmt.paymentDueId,
            });
            if (key === 'escrowReceivable') {
              postings.push({
                kind: 'escrowObligationSettlement',
                id: newPostingId(),
                amount: amt,
                debitAccount: 'escrowObligation',
                creditAccount: 'escrow',
                paymentId,
                paymentDueId: stmt.paymentDueId,
              });
            }
          }
        }
      }
    }

    if (allocation.curtailment && allocation.curtailment > 0) {
      postings.push({
        kind: 'curtailment',
        id: newPostingId(),
        amount: allocation.curtailment,
        debitAccount: 'cash',
        creditAccount: 'principal',
        paymentId,
      });
    }

    if (allocation.futureStatementSuspense && allocation.futureStatementSuspense > 0) {
      postings.push({
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount: allocation.futureStatementSuspense,
        debitAccount: 'cash',
        creditAccount: 'futureStatementSuspense',
        paymentId,
      });
    }

    if (allocation.additionalEscrow && allocation.additionalEscrow > 0) {
      postings.push({
        kind: 'paymentEscrowContribution',
        id: newPostingId(),
        amount: allocation.additionalEscrow,
        debitAccount: 'cash',
        creditAccount: 'escrow',
        paymentId,
      });
    }

    if (allocation.suspense && allocation.suspense > 0) {
      postings.push({
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount: allocation.suspense,
        debitAccount: 'cash',
        creditAccount: 'suspense',
        paymentId,
      });
    }

    if (allocation.unappliedSuspense && allocation.unappliedSuspense > 0) {
      postings.push({
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount: allocation.unappliedSuspense,
        debitAccount: 'cash',
        creditAccount: 'unappliedSuspense',
        paymentId,
      });
    }

    if (allocation.fees) {
      for (const entry of allocation.fees) {
        if (entry.amount <= 0) continue;
        const base = {
          id: newPostingId(),
          amount: entry.amount,
          debitAccount: 'cash' as const,
          creditAccount: 'feeReceivable' as const,
          paymentId,
          feeId: entry.feeId,
        };
        const posting: Posting = entry.paymentDueId
          ? { ...base, kind: 'feePaymentApplication' as const, paymentDueId: entry.paymentDueId }
          : { ...base, kind: 'standaloneFeePaymentApplication' as const };
        postings.push(posting);
      }
    }

    // DR lands in `servicingClearing` (not custodial `cash`) and is
    // paired with an immediate `advanceReimbursement` so the clearing
    // account nets to zero per transaction. See
    // `CorporateAdvancePaymentApplicationPosting`.
    if (allocation.advances) {
      for (const entry of allocation.advances) {
        const totalForAdvance = add(entry.principalAmount, entry.interestAmount);
        if (entry.principalAmount > 0) {
          postings.push({
            kind: 'corporateAdvancePaymentApplication',
            id: newPostingId(),
            amount: entry.principalAmount,
            debitAccount: 'servicingClearing',
            creditAccount: 'advancePrincipal',
            paymentId,
            advanceId: entry.advanceId,
          });
        }
        if (entry.interestAmount > 0) {
          postings.push({
            kind: 'corporateAdvancePaymentApplication',
            id: newPostingId(),
            amount: entry.interestAmount,
            debitAccount: 'servicingClearing',
            creditAccount: 'advanceInterest',
            paymentId,
            advanceId: entry.advanceId,
          });
        }
        if (totalForAdvance > 0) {
          postings.push({
            kind: 'advanceReimbursement',
            id: newPostingId(),
            amount: totalForAdvance,
            debitAccount: 'advanceFunding',
            creditAccount: 'servicingClearing',
            advanceId: entry.advanceId,
          });
        }
      }
    }

    return this.appendTransaction(postings);
  }

  // Callers that need to pre-shape an allocation should call
  // `recordPayoffFromAllocation` directly.
  recordPayoff(
    payoff: Payoff,
    payment: Payment,
    outstandingStatements: ReadonlyArray<PaymentDue>,
  ): Transaction | undefined {
    return this.recordPayoffFromAllocation(
      payoff,
      payment,
      allocatePayoffForLedger(payoff, payment, outstandingStatements, this.index),
    );
  }

  recordPayoffFromAllocation(
    payoff: Payoff,
    payment: Payment,
    allocation: LedgerPayoffAllocation,
  ): Transaction | undefined {
    const postings: Posting[] = [];
    const paymentId = payment.id;
    const payoffId: PayoffId = payoff.id;

    const escrowAssessmentByPaymentDueId = new Map<PaymentDueId, LedgerPostingId>();
    for (const t of this.ledger) {
      for (const p of t.postings) {
        if (
          p.kind === 'statementAssessment' &&
          p.creditAccount === 'escrowObligation' &&
          'paymentDueId' in p &&
          !this.isVoided(p.id)
        ) {
          escrowAssessmentByPaymentDueId.set(p.paymentDueId, p.id);
        }
      }
    }

    for (const stmt of allocation.statements) {
      for (const key of StatementReceivableKeys) {
        const amt = stmt[key];
        if (!amt || amt <= 0) continue;
        if (key === 'escrowReceivable') {
          // Open escrowReceivable at payoff: `Payoff.totalDue` excludes
          // the escrow portion, so the borrower didn't fund it. Cancel
          // the escrow leg of the assessment (DR escrowObligation / CR
          // escrowReceivable, paired via voidsPostingId); real `escrow`
          // stays untouched. See GLAccount.ts for the rationale.
          const matchingAssessment = escrowAssessmentByPaymentDueId.get(stmt.paymentDueId);
          if (!matchingAssessment) {
            // Only reachable on hand-built test snapshots that bypass
            // boarding. An open `escrowReceivable` will trip invariant
            // 8 downstream and surface the issue without throwing.
            continue;
          }
          postings.push({
            kind: 'statementCancellation',
            id: newPostingId(),
            amount: amt,
            debitAccount: 'escrowObligation',
            creditAccount: 'escrowReceivable',
            paymentDueId: stmt.paymentDueId,
            voidsPostingId: matchingAssessment,
          } as Posting);
          continue;
        }
        postings.push({
          kind: 'payoffStatementApplication',
          id: newPostingId(),
          amount: amt,
          debitAccount: 'cash',
          creditAccount: key,
          paymentId,
          payoffId,
          paymentDueId: stmt.paymentDueId,
        });
      }
    }

    if (allocation.outstandingInterest && allocation.outstandingInterest > 0) {
      postings.push({
        kind: 'payoffInterestApplication',
        id: newPostingId(),
        amount: allocation.outstandingInterest,
        debitAccount: 'cash',
        creditAccount: 'interest',
        paymentId,
        payoffId,
      });
    }

    // `Payoff.totalDue` already absorbs the rebate, so payment.total
    // is net of it; this posting nets the gross cash inflow down to
    // the borrower's actual payment.
    if (allocation.outstandingInterestRebate && allocation.outstandingInterestRebate > 0) {
      postings.push({
        kind: 'payoffInterestRebate',
        id: newPostingId(),
        amount: allocation.outstandingInterestRebate,
        debitAccount: 'interest',
        creditAccount: 'cash',
        paymentId,
        payoffId,
      });
    }

    if (allocation.payoffFee && allocation.payoffFee.amount > 0) {
      const { feeId, paymentDueId, amount } = allocation.payoffFee;
      postings.push({
        kind: paymentDueId ? 'feeAssessment' : 'standaloneFeeAssessment',
        id: newPostingId(),
        amount,
        debitAccount: 'feeReceivable',
        creditAccount: 'feeRevenue',
        feeId,
        ...(paymentDueId ? { paymentDueId } : {}),
      } as unknown as Posting);
      postings.push({
        kind: 'payoffFeeApplication',
        id: newPostingId(),
        amount,
        debitAccount: 'cash',
        creditAccount: 'feeReceivable',
        paymentId,
        payoffId,
        feeId,
        ...(paymentDueId ? { paymentDueId } : {}),
      } as unknown as Posting);
    }

    if (allocation.remainingPrincipal > 0) {
      postings.push({
        kind: 'payoffPrincipalApplication',
        id: newPostingId(),
        amount: allocation.remainingPrincipal,
        debitAccount: 'cash',
        creditAccount: 'principal',
        paymentId,
        payoffId,
      });
    }

    // One posting per non-zero bucket; the allocator picks buckets
    // based on actual ledger balances to avoid driving any one
    // bucket negative.
    if (allocation.suspenseApplied) {
      const buckets: ReadonlyArray<['suspense' | 'unappliedSuspense' | 'futureStatementSuspense']> = [
        ['suspense'],
        ['unappliedSuspense'],
        ['futureStatementSuspense'],
      ];
      for (const [bucket] of buckets) {
        const amount = allocation.suspenseApplied[bucket];
        if (!amount || amount <= 0) continue;
        postings.push({
          kind: 'payoffSuspenseApplication',
          id: newPostingId(),
          amount,
          debitAccount: bucket,
          creditAccount: 'principal',
          paymentId,
          payoffId,
        });
      }
    }

    if (allocation.payoffShortfall && allocation.payoffShortfall > 0) {
      postings.push({
        kind: 'payoffShortfall',
        id: newPostingId(),
        amount: allocation.payoffShortfall,
        debitAccount: 'payoffShortfall',
        creditAccount: 'principal',
        paymentId,
        payoffId,
      });
    }

    return this.appendTransaction(postings);
  }

  // Emits only the direct partial-payoff postings. Post-partial
  // statement regeneration and additional-suspense reallocation are
  // handled by separate subhandlers in their own transactions.
  recordPartialPayoff(payoff: Payoff, payment: Payment): Transaction | undefined {
    return this.recordPartialPayoffFromAllocation(payoff, payment, allocatePartialPayoffForLedger(payoff, payment));
  }

  recordPartialPayoffFromAllocation(
    payoff: Payoff,
    payment: Payment,
    allocation: LedgerPartialPayoffAllocation,
  ): Transaction | undefined {
    const postings: Posting[] = [];
    const paymentId = payment.id;
    const payoffId = payoff.id;

    if (allocation.payoffFee && allocation.payoffFee.amount > 0) {
      const { feeId, paymentDueId, amount } = allocation.payoffFee;
      postings.push({
        kind: paymentDueId ? 'feeAssessment' : 'standaloneFeeAssessment',
        id: newPostingId(),
        amount,
        debitAccount: 'feeReceivable',
        creditAccount: 'feeRevenue',
        feeId,
        ...(paymentDueId ? { paymentDueId } : {}),
      } as unknown as Posting);
      postings.push({
        kind: 'payoffFeeApplication',
        id: newPostingId(),
        amount,
        debitAccount: 'cash',
        creditAccount: 'feeReceivable',
        paymentId,
        payoffId,
        feeId,
        ...(paymentDueId ? { paymentDueId } : {}),
      } as unknown as Posting);
    }

    if (allocation.partialPrincipal > 0) {
      postings.push({
        kind: 'payoffPrincipalApplication',
        id: newPostingId(),
        amount: allocation.partialPrincipal,
        debitAccount: 'cash',
        creditAccount: 'principal',
        paymentId,
        payoffId,
      });
    }

    if (allocation.additionalPrincipal && allocation.additionalPrincipal > 0) {
      postings.push({
        kind: 'curtailment',
        id: newPostingId(),
        amount: allocation.additionalPrincipal,
        debitAccount: 'cash',
        creditAccount: 'principal',
        paymentId,
      });
    }

    if (allocation.additionalEscrow && allocation.additionalEscrow > 0) {
      postings.push({
        kind: 'paymentEscrowContribution',
        id: newPostingId(),
        amount: allocation.additionalEscrow,
        debitAccount: 'cash',
        creditAccount: 'escrow',
        paymentId,
      });
    }

    // Parks in `unappliedSuspense`; the post-partial regenerate
    // subhandler reallocates this into the new statement.
    if (allocation.additionalSuspense && allocation.additionalSuspense > 0) {
      postings.push({
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount: allocation.additionalSuspense,
        debitAccount: 'cash',
        creditAccount: 'unappliedSuspense',
        paymentId,
      });
    }

    return this.appendTransaction(postings);
  }

  // Modes:
  //   - **void** (default): cash leaves the loan via the reversal
  //     postings. Webhook-driven failures (which mutate `payment.status`
  //     in place) take this path; `reversalPaymentId` is not required.
  //   - **reverse-to-suspense**: cash stays on the loan, parked in
  //     suspense under `reversalPaymentId` (required).
  //     `suspenseAccount` defaults to `unappliedSuspense`; pass
  //     `suspense` for the partial-payment-buffer semantic.
  //
  // Idempotent: already-reversed postings are skipped.
  recordPaymentReversal(
    reversedPaymentId: PaymentId,
    options: {
      reverseToSuspense?: boolean;
      reversalPaymentId?: PaymentId;
      suspenseAccount?: 'suspense' | 'unappliedSuspense';
    },
  ): Transaction | undefined {
    const { reverseToSuspense = false, reversalPaymentId, suspenseAccount = 'unappliedSuspense' } = options;
    if (reverseToSuspense && !reversalPaymentId) {
      throw new Error(
        'recordPaymentReversal: reversalPaymentId is required when reverseToSuspense is true (the new paymentSuspense posting must be attributed to a payment id)',
      );
    }

    const postings: Posting[] = [];
    let totalCashIn = 0;

    // Sibling postings paired with the payment but not attributed to
    // its paymentId (e.g. servicer-side `advanceReimbursement`). We
    // find them via transactionId + kind.
    const SIBLING_REVERSAL_KIND_MAP: Record<string, string> = {
      advanceReimbursement: 'advanceReimbursementReversal',
    };
    const txnsTouchedByPayment = new Set<LedgerTransactionId>();

    for (const txn of this.ledger) {
      for (const p of txn.postings) {
        const c = p as unknown as {
          kind: string;
          id: LedgerPostingId;
          amount: number;
          debitAccount: string;
          creditAccount: string;
          paymentId?: PaymentId;
          paymentDueId?: PaymentDueId;
          feeId?: FeeId;
          payoffId?: PayoffId;
        };
        if (c.paymentId !== reversedPaymentId) continue;
        if (this.isVoided(c.id)) continue;
        if (!REVERSIBLE_PAYMENT_KIND_SET.has(c.kind)) continue;
        const reversalKind = PAYMENT_REVERSAL_KIND_MAP[c.kind as ReversiblePaymentKind];
        txnsTouchedByPayment.add(txn.id);

        const reversal = {
          ...(p as object),
          kind: reversalKind,
          id: newPostingId(),
          debitAccount: c.creditAccount,
          creditAccount: c.debitAccount,
          voidsPostingId: c.id,
        } as unknown as Posting;
        postings.push(reversal);

        // Both `cash` and `servicingClearing` count as borrower-inflow
        // accounts (matching invariant 2). Without `servicingClearing`,
        // a reverse-to-suspense on a corporate-advance payment would
        // park $0 even though borrower funds had arrived.
        if (c.debitAccount === 'cash' || c.debitAccount === 'servicingClearing') {
          totalCashIn = add(totalCashIn, c.amount);
        }
      }
    }

    // Sibling cleanup pass: servicer-side postings (e.g.
    // `advanceReimbursement`) that don't carry the paymentId are
    // identified by sharing a transaction with a reversed posting.
    // Payoff's escrow-leg `statementCancellation` siblings are
    // restored here too — emit a fresh `statementAssessment` to
    // re-open the receivable, equivalent to "un-voiding" it.
    for (const txn of this.ledger) {
      if (!txnsTouchedByPayment.has(txn.id)) continue;
      for (const p of txn.postings) {
        const c = p as unknown as {
          kind: string;
          id: LedgerPostingId;
          amount: number;
          debitAccount: string;
          creditAccount: string;
          paymentDueId?: PaymentDueId;
        };
        if (this.isVoided(c.id)) continue;
        const siblingReversalKind = SIBLING_REVERSAL_KIND_MAP[c.kind];
        if (siblingReversalKind) {
          postings.push({
            ...(p as object),
            kind: siblingReversalKind,
            id: newPostingId(),
            debitAccount: c.creditAccount,
            creditAccount: c.debitAccount,
            voidsPostingId: c.id,
          } as unknown as Posting);
          continue;
        }
        if (c.kind === 'statementCancellation' && c.paymentDueId) {
          postings.push({
            kind: 'statementAssessment',
            id: newPostingId(),
            amount: c.amount,
            // Swap relative to the cancellation to recover the
            // original assessment direction.
            debitAccount: c.creditAccount,
            creditAccount: c.debitAccount,
            paymentDueId: c.paymentDueId,
          } as unknown as Posting);
        }
      }
    }

    if (reverseToSuspense && totalCashIn > 0 && reversalPaymentId) {
      postings.push({
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount: totalCashIn,
        debitAccount: 'cash',
        creditAccount: suspenseAccount,
        paymentId: reversalPaymentId,
      });
    }

    return this.appendTransaction(postings);
  }

  // TODO(post-migration): not yet called by production handlers.
  // During dual-write the ledger mirrors legacy
  // `tryReallocateSubsequentBreakdowns` case-by-case to stay aligned
  // with `paymentToPaymentDue` for invariant 9; once that retires,
  // `handleReversePayment` and `failOrCancelPayment` both collapse
  // to a single call here. Tested in `reverseAndReapplyChain.test.ts`.
  reverseAndReapplyChain(
    reversals: ReadonlyArray<{
      paymentId: PaymentId;
      reverseToSuspense: boolean;
      reversalPaymentId?: PaymentId;
      suspenseAccount?: 'suspense' | 'unappliedSuspense';
    }>,
    paymentsMade: ReadonlyArray<Payment>,
    paymentsDue: ReadonlyArray<PaymentDue>,
    outstandingFees: ReadonlyArray<Fee> = [],
    outstandingAdvances: ReadonlyArray<Advance> = [],
  ): { subsequentPaymentIds: ReadonlyArray<PaymentId> } {
    if (reversals.length === 0) return { subsequentPaymentIds: [] };

    const clusterIds = new Set(reversals.map((r) => r.paymentId));
    let latestClusterIndex = -1;
    for (let i = 0; i < paymentsMade.length; i += 1) {
      if (clusterIds.has(paymentsMade[i].id) && i > latestClusterIndex) latestClusterIndex = i;
    }
    if (latestClusterIndex < 0) {
      throw new Error(
        `reverseAndReapplyChain: none of the target paymentIds [${reversals.map((r) => r.paymentId).join(', ')}] found in paymentsMade`,
      );
    }

    const isReversed = (id: PaymentId): boolean =>
      paymentsMade.some((p) => isReversalPayment(p) && p.transactionId === id);
    const subsequents = paymentsMade
      .slice(latestClusterIndex + 1)
      .filter((p) => !p.isDeposit && !clusterIds.has(p.id) && !isReversalPayment(p) && !isReversed(p.id));

    // LIFO unwind: subsequents newest-first, then the cluster
    // paymentDate-descending (matches `handleReversePayment`).
    for (let i = subsequents.length - 1; i >= 0; i -= 1) {
      this.recordPaymentReversal(subsequents[i].id, { reverseToSuspense: false });
    }
    const orderedReversals = [...reversals].sort((a, b) => {
      const aIdx = paymentsMade.findIndex((p) => p.id === a.paymentId);
      const bIdx = paymentsMade.findIndex((p) => p.id === b.paymentId);
      const aDate = aIdx >= 0 ? new Date(paymentsMade[aIdx].paymentDate).getTime() : 0;
      const bDate = bIdx >= 0 ? new Date(paymentsMade[bIdx].paymentDate).getTime() : 0;
      return bDate - aDate;
    });
    for (const rev of orderedReversals) {
      this.recordPaymentReversal(rev.paymentId, {
        reverseToSuspense: rev.reverseToSuspense,
        reversalPaymentId: rev.reversalPaymentId,
        suspenseAccount: rev.suspenseAccount,
      });
    }

    for (const sub of subsequents) {
      this.recordPaymentApplications([sub], paymentsDue, outstandingFees, outstandingAdvances);
    }

    return { subsequentPaymentIds: subsequents.map((p) => p.id) };
  }

  // Scope for v1: only `type === 'escrow'` with positive
  // `amounts.escrow`. Other disbursement types involve refund-style
  // postings that interact with the non-negative-cash invariant and
  // are deferred.
  //
  // Splits the amount on the current `escrow` GL balance: portion
  // covered by escrow goes DR escrow / CR cash; any excess (servicer
  // fronts corporate cash) goes DR escrowAdvanceReceivable / CR cash.
  recordDisbursement(disbursement: Disbursement): Transaction | undefined {
    if (disbursement.type !== 'escrow') return undefined;
    const total = disbursement.amounts.escrow;
    if (total <= 0) return undefined;

    // Both `escrow` and `cash` are allowed negative; over-disbursements
    // (servicer fronting corporate cash) are journaled inline.
    // Reclassification to a payable happens at presentation, not here.
    return this.appendTransaction([
      {
        kind: 'escrowDisbursement',
        id: newPostingId(),
        amount: total,
        debitAccount: 'escrow',
        creditAccount: 'cash',
        disbursementId: disbursement.id,
      },
    ]);
  }

  /**
   * Mid-cycle servicing transfer: the borrower's payment was applied
   * to this loan (cash landed, receivables cleared via the regular
   * `recordPaymentApplication` flow), and now this servicer is
   * forwarding the dollars onward to the next servicer. Emits one
   * `forwardedPaymentDisbursement` posting that debits the
   * `transferredFunds` expense-like account and credits `cash` for
   * the full payment.total — the cash leaves custody; receivables
   * stay settled.
   *
   * The legacy snapshot's `outstandingBalance` un-applies the
   * forwarded payment (treats it as "didn't happen on our books"),
   * so legacy outstandingBalance = principal + un-applied-receivable.
   * Invariant 9 reconciles the two by including `transferredFunds`
   * in the ledger-side outstandingBalance:
   *   ledger.outstandingBalance = principal + principalReceivable + transferredFunds.
   * That additive matches legacy because `transferredFunds` carries
   * the dollar amount legacy is restoring on the receivable side.
   *
   * Reads `disbursement.amounts` to determine the forward amount
   * (the disbursement's `total` is negative; `amounts` is the
   * authoritative breakdown of what was forwarded). Returns
   * `undefined` if the total forward amount is zero.
   */
  recordForwardedPaymentDisbursement(disbursement: Disbursement): Transaction | undefined {
    if (disbursement.type !== 'forwardPayment') return undefined;
    const amount = Math.abs(disbursement.total);
    if (amount <= 0) return undefined;
    const postings: Posting[] = [
      {
        kind: 'forwardedPaymentDisbursement',
        id: newPostingId(),
        amount,
        debitAccount: 'transferredFunds',
        creditAccount: 'cash',
        disbursementId: disbursement.id,
      },
    ];
    return this.appendTransaction(postings);
  }

  // For the suspense branch, the originating `payoffId` is recovered
  // from the open `payoffShortfall` posting on the ledger so the
  // chargeoff attributes through to its payoff. Escrow chargeoffs and
  // `statementChargeoff` aren't emitted yet — the Writeoff entity
  // lacks the attribution to wire those cleanly.
  recordWriteoff(writeoff: Writeoff): Transaction | undefined {
    const postings: Posting[] = [];

    if (writeoff.amounts.principal > 0) {
      postings.push({
        kind: 'principalChargeoff',
        id: newPostingId(),
        amount: writeoff.amounts.principal,
        debitAccount: 'chargeoff',
        creditAccount: 'principal',
        writeoffId: writeoff.id,
      });
    }

    if (writeoff.amounts.suspense > 0) {
      const target = this.findOpenPayoffShortfall(writeoff.amounts.suspense);
      if (target) {
        postings.push({
          kind: 'payoffShortfallChargeoff',
          id: newPostingId(),
          amount: target.amount,
          debitAccount: 'chargeoff',
          creditAccount: 'payoffShortfall',
          writeoffId: writeoff.id,
          payoffId: target.payoffId,
        });
      }
    }

    if (postings.length === 0) return undefined;
    return this.appendTransaction(postings);
  }

  private findOpenPayoffShortfall(maxAmount: number): { payoffId: PayoffId; amount: number } | undefined {
    const drainedByPayoff = new Map<PayoffId, number>();
    const openByPayoff = new Map<PayoffId, number>();
    const orderedPayoffIds: PayoffId[] = [];
    for (const txn of this.ledger) {
      for (const p of txn.postings) {
        if (p.kind === 'payoffShortfall' && 'payoffId' in p && p.payoffId) {
          if (!openByPayoff.has(p.payoffId)) orderedPayoffIds.push(p.payoffId);
          openByPayoff.set(p.payoffId, (openByPayoff.get(p.payoffId) ?? 0) + p.amount);
        } else if (p.kind === 'payoffShortfallChargeoff' && 'payoffId' in p && p.payoffId) {
          drainedByPayoff.set(p.payoffId, (drainedByPayoff.get(p.payoffId) ?? 0) + p.amount);
        }
      }
    }
    for (const payoffId of orderedPayoffIds) {
      const open = (openByPayoff.get(payoffId) ?? 0) - (drainedByPayoff.get(payoffId) ?? 0);
      if (open > 0) {
        return { payoffId, amount: Math.min(open, maxAmount) };
      }
    }
    return undefined;
  }

  /** No event issues this today (writeoff status is fixed at 'active');
   *  primitive is here for a future cancellation flow. */
  recordWriteoffReversal(reversedWriteoffId: WriteoffId): Transaction | undefined {
    return this.recordReversalsFor('writeoffId', reversedWriteoffId);
  }

  recordDisbursementReversal(reversedDisbursementId: DisbursementId): Transaction | undefined {
    return this.recordReversalsFor('disbursementId', reversedDisbursementId);
  }

  // Same shape as `boardingPrincipal`. `cash` is untouched — funds
  // going to the borrower don't flow through the loan's custodial
  // pool.
  recordDraw(draw: Draw): Transaction | undefined {
    if (draw.isReversal) return undefined;
    if (draw.amount <= 0) return undefined;
    return this.appendTransaction([
      {
        kind: 'draw',
        id: newPostingId(),
        amount: draw.amount,
        debitAccount: 'principal',
        creditAccount: 'noteHolder',
        drawId: draw.id,
      },
    ]);
  }

  recordDrawReversal(reversedDrawId: DrawId): Transaction | undefined {
    return this.recordReversalsFor('drawId', reversedDrawId);
  }

  // Per-type rollups (late / NSF / etc.) come from joining `feeId`
  // against `Fee.feeType` at report time, not per-type accounts.
  recordFeeAssessment(fee: Fee): Transaction | undefined {
    if (fee.totalAmount <= 0) return undefined;
    const base = {
      id: newPostingId(),
      amount: fee.totalAmount,
      debitAccount: 'feeReceivable' as const,
      creditAccount: 'feeRevenue' as const,
      feeId: fee.id,
    };
    const posting: Posting = fee.paymentDueId
      ? { ...base, kind: 'feeAssessment' as const, paymentDueId: fee.paymentDueId }
      : { ...base, kind: 'standaloneFeeAssessment' as const };
    return this.appendTransaction([posting]);
  }

  recordAdvanceCancellation(advanceId: AdvanceId): Transaction | undefined {
    return this.recordReversalsFor('advanceId', advanceId);
  }

  // Absorbs an implicit escrow shortfall (negative `escrow`) into
  // `advanceEscrow`. Reversal: `recordAdvanceCancellation(advanceId)`
  // scopes by `advanceId` and voids this posting alongside any
  // other advance-attributed postings for the same `advanceId`.
  recordEscrowAdvancePromotion(advanceId: AdvanceId, amount: number): Transaction | undefined {
    if (amount <= 0) return undefined;
    return this.appendTransaction([
      {
        kind: 'escrowAdvancePromotion',
        id: newPostingId(),
        amount,
        debitAccount: 'advanceEscrow',
        creditAccount: 'escrow',
        advanceId,
      },
    ]);
  }

  recordFeeCancellation(feeId: FeeId): Transaction | undefined {
    return this.recordReversalsFor('feeId', feeId);
  }

  // The cap reads from the ledger (not `fee.amountRemaining`) so
  // derivation gets the right answer — at re-derive time the legacy
  // field is zero (already marked paid) but the ledger still has the
  // original `feeReceivable` open.
  recordFeePayment(payment: Payment, fee: Fee): Transaction | undefined {
    const amount = paymentAmount(payment);
    const remaining = this.remainingFeeReceivable(fee);
    const draw = add(Math.min(amount, remaining));
    return this.appendTransaction(this.buildFeePaymentPostings(payment, draw > 0 ? [{ fee, amount: draw }] : []));
  }

  // No fallback to legacy `fee.amountRemaining` / `advance.*` —
  // ledger is the source of truth. A missing entry means the ledger
  // hasn't seen the assessment/advance yet and the right cure is to
  // rederive, not to silently double-count against legacy state.
  private remainingFeeReceivable(fee: Fee): number {
    return Math.max(this.index.feeReceivable(fee.id) ?? 0, 0);
  }

  private remainingAdvance(advance: Advance): { principal: number; interest: number } {
    const ledgerRemaining = this.index.advanceBalances(asAdvanceId(advance));
    return {
      principal: Math.max(ledgerRemaining?.advancePrincipal ?? 0, 0),
      interest: Math.max(ledgerRemaining?.advanceInterest ?? 0, 0),
    };
  }

  // Chapter 13 pre-petition receipt: the full payment is held in the `prePetitionUnappliedSuspense`
  // liability (cash -> prePetitionUnappliedSuspense) until reallocated onto pre-petition statements.
  recordPrePetitionPayment(payment: Payment): Transaction | undefined {
    const amount = paymentAmount(payment);
    if (amount <= 0) return undefined;
    return this.appendTransaction([
      {
        kind: 'paymentSuspense',
        id: newPostingId(),
        amount,
        debitAccount: 'cash',
        creditAccount: 'prePetitionUnappliedSuspense',
        paymentId: payment.id,
      },
    ]);
  }

  // Caller controls the order (typically oldest-fee-first).
  recordMultipleFeesPayment(payment: Payment, fees: ReadonlyArray<Fee>): Transaction | undefined {
    let remaining = paymentAmount(payment);
    const entries: { fee: Fee; amount: number }[] = [];
    for (const fee of fees) {
      if (remaining <= 0) break;
      const feeRemaining = this.remainingFeeReceivable(fee);
      const draw = add(Math.min(remaining, feeRemaining));
      if (draw <= 0) continue;
      remaining = subtract(remaining, draw);
      entries.push({ fee, amount: draw });
    }
    return this.appendTransaction(this.buildFeePaymentPostings(payment, entries));
  }

  private buildFeePaymentPostings(payment: Payment, entries: ReadonlyArray<{ fee: Fee; amount: number }>): Posting[] {
    const debitAccount: 'cash' | 'unappliedSuspense' | 'futureStatementSuspense' | 'prePetitionUnappliedSuspense' =
      payment.debitSource?.type === 'balance' ? payment.debitSource.balance : 'cash';
    const paymentId = payment.id;
    const postings: Posting[] = [];
    for (const { fee, amount } of entries) {
      if (amount <= 0) continue;
      const base = {
        id: newPostingId(),
        amount,
        debitAccount,
        creditAccount: 'feeReceivable' as const,
        paymentId,
        feeId: fee.id,
      };
      const posting: Posting = fee.paymentDueId
        ? { ...base, kind: 'feePaymentApplication' as const, paymentDueId: fee.paymentDueId }
        : { ...base, kind: 'standaloneFeePaymentApplication' as const };
      postings.push(posting);
    }
    return postings;
  }

  // The credit side is `advanceFunding` (servicer-fronted liability),
  // not the loan's `principal` asset. See the spec.
  recordAdvance(advance: Advance): Transaction | undefined {
    const postings: Posting[] = [];
    const advanceId = asAdvanceId(advance);
    if (advance.originalPrincipal > 0) {
      postings.push({
        kind: 'corporateAdvance',
        id: newPostingId(),
        amount: advance.originalPrincipal,
        debitAccount: 'advancePrincipal',
        creditAccount: 'advanceFunding',
        advanceId,
      });
    }
    if (advance.advanceInterestCharged > 0) {
      postings.push({
        kind: 'corporateAdvance',
        id: newPostingId(),
        amount: advance.advanceInterestCharged,
        debitAccount: 'advanceInterest',
        creditAccount: 'advanceFunding',
        advanceId,
      });
    }
    return this.appendTransaction(postings);
  }

  // Splits the payment across `advancePrincipal` first, then
  // `advanceInterest`. Paired with `advanceReimbursement` so the net
  // effect on `cash` is zero (servicer recovers immediately).
  recordAdvancePayment(payment: Payment, advance: Advance): Transaction | undefined {
    const advanceId = asAdvanceId(advance);
    const amount = paymentAmount(payment);
    const remaining = this.remainingAdvance(advance);
    const principalAmount = add(Math.min(Math.max(amount, 0), remaining.principal));
    const afterPrincipal = subtract(amount, principalAmount);
    const interestAmount = add(Math.min(Math.max(afterPrincipal, 0), remaining.interest));
    const totalForAdvance = add(principalAmount, interestAmount);
    const paymentId = payment.id;
    const postings: Posting[] = [];
    if (principalAmount > 0) {
      postings.push({
        kind: 'corporateAdvancePaymentApplication',
        id: newPostingId(),
        amount: principalAmount,
        debitAccount: 'servicingClearing',
        creditAccount: 'advancePrincipal',
        paymentId,
        advanceId,
      });
    }
    if (interestAmount > 0) {
      postings.push({
        kind: 'corporateAdvancePaymentApplication',
        id: newPostingId(),
        amount: interestAmount,
        debitAccount: 'servicingClearing',
        creditAccount: 'advanceInterest',
        paymentId,
        advanceId,
      });
    }
    if (totalForAdvance > 0) {
      // No `paymentId` attribution: the reimbursement is the
      // servicer's action, correlated via the shared transactionId.
      postings.push({
        kind: 'advanceReimbursement',
        id: newPostingId(),
        amount: totalForAdvance,
        debitAccount: 'advanceFunding',
        creditAccount: 'servicingClearing',
        advanceId,
      });
    }
    return this.appendTransaction(postings);
  }

  // ── Internal ──────────────────────────────────────────────────────

  // Generic reversal emitter driven by `reversalOf(..., scopeIdField)`
  // registrations in `Posting.ts`. `recordPaymentReversal` is bespoke
  // (has the reverse-to-suspense variant); the simpler reversal
  // recorders delegate here.
  private recordReversalsFor(idField: ReversalScopeIdField, idValue: string): Transaction | undefined {
    const kindMap = REVERSAL_KIND_BY_ID_FIELD[idField];
    if (!kindMap) return undefined;
    const postings: Posting[] = [];
    for (const txn of this.ledger) {
      for (const p of txn.postings) {
        const c = p as unknown as { kind: string; id: LedgerPostingId; debitAccount: string; creditAccount: string };
        const matchingId = (p as unknown as Record<string, unknown>)[idField];
        if (matchingId !== idValue) continue;
        if (this.isVoided(c.id)) continue;
        const reversalKind = kindMap[c.kind];
        if (!reversalKind) continue;
        postings.push({
          ...(p as object),
          kind: reversalKind,
          id: newPostingId(),
          debitAccount: c.creditAccount,
          creditAccount: c.debitAccount,
          voidsPostingId: c.id,
        } as unknown as Posting);
      }
    }
    return this.appendTransaction(postings);
  }

  private appendTransaction(postings: Posting[]): Transaction | undefined {
    if (!isNonEmptyArray(postings)) return undefined;
    const txn: Transaction = {
      id: newTransactionId(),
      eventId: this.ctx.eventId,
      effectiveDate: this.ctx.effectiveDate,
      recordedAt: this.ctx.recordedAt,
      postings,
    };
    this.ledger.push(txn);
    for (const p of postings) {
      this.index.recordPosting(p);
    }
    return txn;
  }
}

// ── Date helpers callers can use to build a `LedgerEventContext` ────

// Project instant dates to UTC midnight before handing off — backdated
// / timezone-sensitive cases are deferred (see spec §Date normalization).
export const paymentDateToTemporalPlainDate = (d: Date): Temporal.PlainDate => {
  const zdt = toTemporalInstant(d).toZonedDateTimeISO('UTC');
  return jsPlainDateToTemporalPlainDate(new Date(zdt.year, zdt.month - 1, zdt.day), { errorOnNonZeroTime: false });
};

export const jsPlainDateValueToTemporalPlainDate = (d: Date): Temporal.PlainDate =>
  jsPlainDateToTemporalPlainDate(d, { errorOnNonZeroTime: false });
